import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { F, fC, fN, r2, moTrend, RANGE_LABEL } from '@/dashboard/data';
import { SecHead, MetCard, CT, Tag } from '@/dashboard/ui';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';

/* ═══════════════════ ORGANIC TAB ═══════════════════ */
export function OrganicTab({ data }) {
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const mo = data.monthly;
  const ga4 = data.organic;
  const kws = data.keywords;
  const cl = data.clarity;
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => moTrend(data.monthly, key);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div>
        <SecHead title="GA4 - Website Analytics" accent="#E8710A" right="Live sync" />
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
          <MetCard label="Sessions" value={fN(ga4.sessions)} col="#E8710A" trend={tr("sessions")} chartData={mo} dateRange={rl} chartKey="sessions" valueFormat="count" />
          <MetCard label="Users" value={fN(ga4.users)} sub="72% of sessions" col="#FFD166" />
          <MetCard label="New Users %" value={`${ga4.newUsers}%`} col="#00D4FF" trend={tr("ntb")} chartData={mo} dateRange={rl} chartKey="ntb" valueFormat="pct" />
          <MetCard label="Bounce Rate" value={`${ga4.bounceRate}%`} col={ga4.bounceRate<40?'#06D6A0':ga4.bounceRate<55?'#FFD166':'#FF4444'} />
          <MetCard label="Avg Duration" value={ga4.avgDur} col="#C77DFF" />
          <MetCard label="Pages/Session" value={String(ga4.pps)} col="#06D6A0" />
          <MetCard label="Conversions" value={fN(ga4.goalConv)} col="#06D6A0" trend={tr("conversions")} chartData={mo} dateRange={rl} chartKey="conversions" valueFormat="count" />
          <MetCard label="Conv. Rate" value={`${ga4.convRate}%`} sub={`${fN(ga4.goalConv)} conversions / ${fN(ga4.sessions)} sessions`} col={ga4.convRate>=2?'#06D6A0':'#FFD166'} />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'2fr 1fr', gap:14 }}>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:8, padding:isMobile?12:18, overflowX:'auto' }}>
          <SecHead title="Google Search Console" accent="#34A853" />
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, fontFamily:F.mono, minWidth:500 }}>
            <thead><tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {['Keyword','Pos','Change','Impressions','Clicks','CTR'].map(h =>
                <th key={h} style={{ padding:'8px 9px', color:T.textSecondary, textAlign:'left', fontSize:10, textTransform:'uppercase', fontWeight:600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {kws.map((k,i) => {
                const d = (k.prev - k.pos).toFixed(1); const up = parseFloat(d) > 0;
                return (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 9px', color:T.text }}>{k.kw}</td>
                    <td style={{ padding:'8px 9px', color:k.pos<=3?'#06D6A0':k.pos<=10?'#FFD166':T.muted, fontWeight:700 }}>#{k.pos}</td>
                    <td style={{ padding:'8px 9px', color:up?'#06D6A0':'#FF4444' }}>{up?'\u25B2':'\u25BC'}{Math.abs(d)}</td>
                    <td style={{ padding:'8px 9px', color:T.muted }}>{fN(k.impr)}</td>
                    <td style={{ padding:'8px 9px', color:T.textSecondary }}>{fN(k.clicks)}</td>
                    <td style={{ padding:'8px 9px', color:k.ctr>=5?'#06D6A0':T.muted }}>{k.ctr}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Clarity UX Intelligence" accent="#00B4D8" />
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[
              { label:'Rage Clicks', value:cl.rage, col:'#FF4444', note:'UX friction detected' },
              { label:'Dead Clicks', value:cl.dead, col:'#FF9F43', note:'Non-interactive elements' },
              { label:'Quick Backs', value:cl.qbacks, col:'#FFD166', note:'Immediate back nav' },
              { label:'Scroll Depth', value:`${cl.scroll}%`, col:'#06D6A0', note:'Content engagement' },
              { label:'JS Errors', value:cl.err, col:cl.err>10?'#FF4444':T.muted, note:'Unique error types' },
              { label:'Recordings', value:fN(cl.rec), col:'#00B4D8', note:'Available to review' },
            ].map(m => (
              <div key={m.label} style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 10px',
                background:T.surfaceAlpha, borderRadius:5, border:`1px solid ${T.border}` }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:T.textSecondary, textTransform:'uppercase', letterSpacing:'.06em', fontFamily:F.mono, fontWeight:600 }}>{m.label}</div>
                  <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono }}>{m.note}</div>
                </div>
                <div style={{ fontSize:18, fontWeight:700, color:m.col, fontFamily:F.mono }}>
                  {typeof m.value==='number'?m.value.toLocaleString():m.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
        <SecHead title="Sessions & Leads - 12M Organic Trend" accent="#34A853" />
        <ResponsiveContainer width="100%" height={isMobile?150:180}>
          <AreaChart data={mo}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
            <XAxis dataKey="month" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
            <YAxis tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}} tickFormatter={fN}/>
            <Tooltip content={<CT/>}/>
            <Area type="monotone" dataKey="sessions" name="Sessions" fill="rgba(52,168,83,.1)" stroke="#34A853" strokeWidth={2}/>
            <Area type="monotone" dataKey="leads" name="Leads" fill="rgba(0,212,255,.06)" stroke="#00D4FF" strokeWidth={1.5} strokeDasharray="5 3"/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ═══════════════════ CRM TAB ═══════════════════ */
export function CRMTab({ data }) {
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const c = data.crm;
  const mo = data.monthlyForecast;
  const ltvcac = r2(c.ltv/c.cac);
  const isB2B = data.kpis.kind === 'b2b';
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => moTrend(data.monthly, key);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div>
        <SecHead title="CRM Pipeline - HubSpot / Salesforce / Zoho" accent="#FF7A59" right="Live sync" />
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
          {isB2B && <MetCard label="Pipeline Value" value={fC(c.pipelineValue)} sub={`${c.openOpportunities} open opportunities`} col="#06D6A0" trend={tr("pipeline")} chartData={mo} dateRange={rl} chartKey="pipeline" valueFormat="currency" />}
          <MetCard label="Avg Deal Size" value={fC(c.avgDealSize)} sub="Contract value" col="#FFD166" chartData={mo} dateRange={rl} chartKey="aov" valueFormat="currency" trend={tr("aov")} />
          <MetCard label="Win Rate" value={`${c.winRate}%`} col={c.winRate>=30?'#06D6A0':'#FFD166'} trend={0} chartData={mo} dateRange={rl} chartKey="roi" valueFormat="pct" />
          <MetCard label="Sales Cycle" value={`${c.salesCycle}d`} col={c.salesCycle<30?'#06D6A0':'#FFD166'} />
          <MetCard label="CAC" value={fC(c.cac)} col="#FF6B6B" trend={tr("cac")} chartData={mo} dateRange={rl} chartKey="cac" valueFormat="currency" />
          <MetCard label="LTV" value={fC(c.ltv)} col="#00D4FF" trend={tr("ltv")} chartData={mo} dateRange={rl} chartKey="ltv" valueFormat="currency" />
          <MetCard label="LTV : CAC" value={`${ltvcac}x`} col={parseFloat(ltvcac)>=5?'#06D6A0':'#FFD166'} trend={0} chartData={mo} dateRange={rl} chartKey="ltvCac" valueFormat="ratio" />
          <MetCard label="MQL to SQL" value={`${c.mqlToSql}%`} sub="Stage conversion" col="#C77DFF" />
          {isB2B && <MetCard label="Churn Rate" value={`${c.churn}%`} col={c.churn<5?'#06D6A0':c.churn<10?'#FFD166':'#FF4444'} />}
          {!isB2B && <MetCard label="Repeat Rate" value={`${r2(100 - data.kpis.totalNTB)}%`} sub="Returning customers" col="#06D6A0" />}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14 }}>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Sales Funnel" accent="#FF6B6B" />
          {c.funnel.map((s,i) => {
            const max = c.funnel[0].value;
            const pct = (s.value/max*100).toFixed(1);
            const drop = i>0?((c.funnel[i-1].value-s.value)/c.funnel[i-1].value*100).toFixed(0):null;
            return (
              <div key={s.stage} style={{ display:'flex', alignItems:'center', gap:9, marginBottom:7 }}>
                <div style={{ width:88, fontSize:11, color:T.textSecondary, textAlign:'right', fontFamily:F.mono, textTransform:'uppercase', flexShrink:0, fontWeight:500 }}>{s.stage}</div>
                <div style={{ flex:1, height:19, background:T.surfaceAlpha, borderRadius:2, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:s.col, opacity:.85, display:'flex', alignItems:'center', paddingLeft:7 }}>
                    <span style={{ fontSize:12, color:T.funnelText, fontWeight:700, fontFamily:F.mono, whiteSpace:'nowrap' }}>{fN(s.value)}</span>
                  </div>
                </div>
                {drop ? <div style={{ width:34, fontSize:9, color:'#FF6B6B', fontFamily:F.mono, textAlign:'right', flexShrink:0 }}>{'\u2193'}{drop}%</div> : <div style={{width:34}}/>}
              </div>
            );
          })}
        </div>

        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Revenue: Actual vs Forecast" accent="#06D6A0" />
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={mo}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
              <XAxis dataKey="month" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
              <YAxis tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}} tickFormatter={fC}/>
              <Tooltip content={<CT/>}/>
              <Bar dataKey="revenue" name="Actual" fill="rgba(6,214,160,.35)" stroke="#06D6A0" strokeWidth={1}/>
              <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#FFD166" strokeWidth={2} strokeDasharray="5 3" dot={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ COMPETITIVE TAB ═══════════════════ */
export function CompTab({ data }) {
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const sovTotal = data.compSOV.reduce((a,d) => a+d.sov, 0);
  const norm = data.compSOV.map(d => ({ ...d, sov:+(d.sov/sovTotal*100).toFixed(1) }));
  const cp = data.competitive;
  const chSOV = cp.byChannel;
  const rl = RANGE_LABEL[data.range] || data.range;
  const tr = key => moTrend(data.monthly, key);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
        <MetCard label="Overall SOV" value={`${norm[0]?.sov}%`} sub="Paid & organic blended" col="#00D4FF" trend={tr("sov")} chartData={data.monthly} dateRange={rl} chartKey="sov" valueFormat="pct" />
        <MetCard label="Paid SOV" value={`${cp.paidSOV}%`} sub="Ad impression share" col="#4285F4" trend={tr("sov")} chartData={data.monthly} dateRange={rl} chartKey="sov" valueFormat="pct" />
        <MetCard label="Organic SOV" value={`${cp.organicSOV}%`} sub="Search visibility" col="#34A853" />
        <MetCard label="SOV Rank" value={`#${cp.rank}`} sub="In your category" col="#FFD166" />
        <MetCard label="Imp. Share Lost (Budget)" value={`${cp.impShareLostBudget}%`} sub="Raise budgets to recover" col="#FF4444" />
        <MetCard label="Imp. Share Lost (Rank)" value={`${cp.impShareLostRank}%`} sub="Improve rank to recover" col="#FF9F43" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14 }}>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="Category SOV Breakdown" accent="#00D4FF" />
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={norm} dataKey="sov" nameKey="name" cx="50%" cy="50%" outerRadius={88} innerRadius={46} paddingAngle={2}>
                {norm.map((d,i) => <Cell key={i} fill={d.col} opacity={0.9}/>)}
              </Pie>
              <Tooltip content={<CT/>} formatter={v=>`${v}%`}/>
              <Legend wrapperStyle={{fontSize:10,fontFamily:F.mono,color:T.muted}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
          <SecHead title="SOV by Channel - You vs Competitors" accent="#C77DFF" />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chSOV} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} horizontal={false}/>
              <XAxis type="number" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}}/>
              <YAxis type="category" dataKey="channel" tick={{fill:T.axisTick,fontSize:11,fontFamily:F.mono}} width={100}/>
              <Tooltip content={<CT/>}/>
              <Bar dataKey="yours" name="Your Brand" fill="#00D4FF" opacity={0.9}/>
              <Bar dataKey="compA" name="Comp. A" fill="#FF6B6B" opacity={0.7}/>
              <Bar dataKey="compB" name="Comp. B" fill="#FFD166" opacity={0.6}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:10, padding:18, boxShadow:T.elev1 }}>
        <SecHead title="SOV Trend - 12M vs Category Average" accent="#FFD166" />
        <ResponsiveContainer width="100%" height={isMobile?140:170}>
          <LineChart data={data.monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine}/>
            <XAxis dataKey="month" tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}}/>
            <YAxis tick={{fill:T.axisTick,fontSize:10,fontFamily:F.mono}} domain={[0,50]}/>
            <Tooltip content={<CT/>}/>
            <Line type="monotone" dataKey="sov" name="Your SOV %" stroke="#00D4FF" strokeWidth={2.5} dot={false}/>
            <ReferenceLine y={22} stroke="rgba(255,209,102,.5)" strokeDasharray="6 3"
              label={{value:'Category Avg',fill:'#FFD166',fontSize:11,fontFamily:F.mono,position:'right'}}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ═══════════════════ BLINDSPOTS TAB ═══════════════════ */
export function BlindspotsTab({ data }) {
  const [flt, setFlt] = useState('all');
  const { colors: T } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const spots = flt === 'all' ? data.blindSpots : data.blindSpots.filter(b => b.sev === flt);
  const total = data.blindSpots.reduce((a,b) => a+b.impact, 0);
  const rl = RANGE_LABEL[data.range] || data.range;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(168px,1fr))', gap:8 }}>
        <MetCard label="Total Recoverable" value={fC(total)} sub="Est. monthly leakage" col="#FF4444" chartData={data.monthly} dateRange={rl} chartKey="leakage" valueFormat="currency" />
        <MetCard label="Critical Issues" value={String(data.blindSpots.filter(b=>b.sev==='critical').length)} col="#FF4444" />
        <MetCard label="High Priority" value={String(data.blindSpots.filter(b=>b.sev==='high').length)} col="#FF9F43" />
        <MetCard label="Medium Priority" value={String(data.blindSpots.filter(b=>b.sev==='medium').length)} col="#FFD166" />
        <MetCard label="Strong Spots" value={String(data.strongSpots.length)} col="#06D6A0" />
      </div>

      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
        {['all','critical','high','medium'].map(f => {
          const col = {all:'#00D4FF',critical:'#FF4444',high:'#FF9F43',medium:'#FFD166'}[f];
          return (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFlt(f)}
              style={{ padding:'4px 14px', borderRadius:4, border:flt===f?`1px solid ${col}`:`1px solid ${T.border}`,
                background:flt===f?`${col}14`:'transparent', color:flt===f?col:T.muted,
                fontSize:10, cursor:'pointer', fontFamily:F.mono, textTransform:'capitalize' }}>
              {f==='all'?'All Issues':f}
            </button>
          );
        })}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14 }}>
        <div>
          <SecHead title="Revenue Blind Spots" accent="#FF4444" />
          {spots.map(b => {
            const sc = {critical:'#FF4444',high:'#FF9F43',medium:'#FFD166'}[b.sev]||T.muted;
            return (
              <div key={b.id} data-testid={`blindspot-${b.id}`}
                style={{ background:`${sc}08`, border:`1px solid ${sc}22`, borderLeft:`3px solid ${sc}`,
                  borderRadius:'0 8px 8px 0', padding:'13px 15px', marginBottom:9 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:5 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:T.cardText, fontFamily:F.head, lineHeight:1.3, maxWidth:'65%' }}>{b.title}</div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:15, color:'#06D6A0', fontFamily:F.mono, fontWeight:700 }}>+{fC(b.impact)}</div>
                    <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono }}>est/mo</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:5, marginBottom:7 }}>
                  <Tag col={sc}>{b.sev}</Tag><Tag col={T.muted}>{b.ch}</Tag>
                </div>
                <div style={{ fontSize:13, color:T.textSecondary, lineHeight:1.6, marginBottom:8 }}>{b.desc}</div>
                <div style={{ fontSize:12, color:T.textSecondary, borderTop:`1px solid ${T.border}`, paddingTop:7 }}>
                  <span style={{ color:'#00D4FF', fontWeight:600 }}>Action: </span>{b.action}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <SecHead title="Strong Spots - Amplify" accent="#06D6A0" />
          {data.strongSpots.map(s => (
            <div key={s.id} style={{ background:'rgba(6,214,160,.05)', border:'1px solid rgba(6,214,160,.1)',
              borderLeft:`3px solid ${s.col}`, borderRadius:'0 8px 8px 0', padding:'13px 15px', marginBottom:9 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:5 }}>
                <div style={{ fontSize:15, fontWeight:700, color:T.cardText, fontFamily:F.head, lineHeight:1.3, maxWidth:'60%' }}>{s.title}</div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:15, color:'#06D6A0', fontFamily:F.mono, fontWeight:700 }}>{s.metric}</div>
                  <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono }}>{s.bench}</div>
                </div>
              </div>
              <Tag col={s.col}>{s.ch}</Tag>
              <div style={{ fontSize:13, color:T.textSecondary, lineHeight:1.6, marginTop:8 }}>{s.desc}</div>
            </div>
          ))}

          <div style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderRadius:8, padding:14, marginTop:4 }}>
            <SecHead title="Recovery Impact by Channel" accent="#FFD166" />
            <ResponsiveContainer width="100%" height={155}>
              <BarChart data={data.blindSpots} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} horizontal={false}/>
                <XAxis type="number" tick={{fill:T.axisTick,fontSize:8,fontFamily:F.mono}} tickFormatter={fC}/>
                <YAxis type="category" dataKey="ch" tick={{fill:T.axisTick,fontSize:8,fontFamily:F.mono}} width={86}/>
                <Tooltip content={<CT/>} formatter={v=>fC(v)}/>
                <Bar dataKey="impact" name="Recovery" fill="#FFD166" opacity={0.8}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
